/**
 * @module common/areaResolver
 * @description 지역 마스터(`getArea()` 응답)를 유일한 진실 원천으로 삼아 지역 코드를 해석한다.
 *
 * 배경 — 2026-09-15 운영 장애:
 *   기존 `RepairRequestRegistration.setArea()` 는 주소 문자열을 정규식으로 파싱해 시/도·시/군/구
 *   이름을 뽑고, **마스터 조회에 실패하면 파싱한 원문을 그대로 코드 필드에 대입**했다.
 *   그 결과 `area_main_code = area_sub_code = '여의서로43'` 같은 값이 FK 로 저장되었고,
 *   단 1건이 접수 목록 페이지 전체를 EntityNotFoundException(500) 으로 죽였다.
 *
 * 이 모듈의 불변식:
 *   **주소 문자열이 코드가 되는 경로는 존재하지 않는다.**
 *   `resolveAreaByAddress` / `resolveAreaBySigunguCode` 가 돌려주는 코드는 언제나 `areaList` 에
 *   실재하는 항목에서 꺼낸 값이고, 찾지 못하면 빈 값이다. 장애의 오염원이던 "추정 실패 시 원문 대입"은
 *   구조적으로 재현될 수 없다.
 *
 *   예외는 `resolveNewAreaFromPostcode` 하나뿐이다. 마스터에 없는 코드를 돌려주지만 출처가
 *   주소 API 의 구조화된 `sigunguCode` 필드이지 문자열 추정이 아니며, BE 와 같은 형식 관문을 통과한
 *   값만 낸다. 신설 행정구역이 마스터에 없어 접수가 막히는 것을 풀기 위한 의도된 통로다
 *   (자세한 근거는 해당 함수 주석).
 */

/** 해석 실패 시 반환하는 빈 결과 (원문을 코드로 쓰지 않는다) */
export const EMPTY_AREA = Object.freeze({
  areaMainCode: '',
  areaSubCode: '',
  areaMainName: '',
  areaSubName: ''
})

/**
 * 같은 시/도를 가리키는 표기들의 동치 그룹.
 *
 * 다음(카카오) 우편번호 API 의 `sido` 는 표기가 하나로 고정돼 있지 않다 (2026-09-15 실측):
 *   - 구 행정구역은 약칭  : `'서울'`, `'경기'`
 *   - 개편된 신규 명칭은 전체명: `'강원특별자치도'`
 * 마스터(`area.main_name`) 역시 행정구역 개편에 따라 표기가 바뀐다(강원도 → 강원특별자치도).
 *
 * 그래서 어느 한쪽 표기를 정답으로 고정하지 않고 **동치 그룹으로 비교만** 한다.
 * 실제로 사용하는 코드·이름은 언제나 마스터 항목에서 꺼내므로 마스터 표기가 무엇이든 동작한다.
 */
const SIDO_GROUPS = [
  ['서울특별시', '서울시', '서울'],
  ['부산광역시', '부산시', '부산'],
  ['대구광역시', '대구시', '대구'],
  ['인천광역시', '인천시', '인천'],
  ['광주광역시', '광주시', '광주'],
  ['대전광역시', '대전시', '대전'],
  ['울산광역시', '울산시', '울산'],
  ['세종특별자치시', '세종시', '세종'],
  ['경기도', '경기'],
  ['강원특별자치도', '강원도', '강원'],
  ['충청북도', '충북'],
  ['충청남도', '충남'],
  ['전북특별자치도', '전라북도', '전북'],
  ['전라남도', '전남'],
  ['경상북도', '경북'],
  ['경상남도', '경남'],
  ['제주특별자치도', '제주도', '제주']
]

/** 시/도 표기 → 동치 그룹 키(그룹 대표 표기). 미등록 표기는 `''`. */
const SIDO_KEY_BY_ALIAS = SIDO_GROUPS.reduce((acc, group) => {
  group.forEach((alias) => { acc[alias] = group[0] })
  return acc
}, {})

/** 시군구 코드 형식 (BE `AreaResolver.SUB_CODE_FORMAT` 과 같은 관문) */
const SUB_CODE_FORMAT = /^\d{5}$/
/** 마스터에 기록해도 되는 시군구명 형식 (BE `AreaResolver.SUB_NAME_FORMAT` 과 같은 관문) */
const SUB_NAME_FORMAT = /^[가-힣A-Za-z0-9 ]{1,20}$/

/** 연속 공백을 하나로 줄이고 앞뒤를 다듬는다. */
function normalizeSpace(value) {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim()
}

/**
 * 시/도 표기를 동치 그룹 키로 바꾼다.
 * @param {string} name - '서울' / '서울특별시' / '강원특별자치도' 등
 * @returns {string} 그룹 키. 시/도 표기가 아니면 빈 문자열
 */
function sidoKey(name) {
  return SIDO_KEY_BY_ALIAS[normalizeSpace(name)] || ''
}

/**
 * 마스터의 시/도명과 주소의 시/도 표기가 같은 곳을 가리키는지 본다.
 *
 * 표기가 그대로 같으면 동치 그룹을 보지 않는다. 그래야 위 목록에 없는 시/도가
 * 마스터에 생겨도(행정구역 개편·통합) 프론트 수정 없이 해석된다 —
 * 하드코딩 목록이 마스터를 못 따라가 조용히 실패하는 일을 막는다.
 * 목록은 표기가 서로 다를 때('서울' vs '서울특별시')만 쓰는 보조 수단이다.
 *
 * @param {string} masterMainName - 마스터 항목의 mainName
 * @param {string} addressHead - 주소 첫 토큰
 * @returns {boolean}
 */
function sidoMatches(masterMainName, addressHead) {
  const master = normalizeSpace(masterMainName)
  const head = normalizeSpace(addressHead)
  if (!master || !head) return false
  if (master === head) return true

  const key = sidoKey(master)
  return key !== '' && key === sidoKey(head)
}

/** 마스터 항목 → 해석 결과 (코드·이름 모두 마스터 원본 값을 그대로 쓴다) */
function toResult(area) {
  return {
    areaMainCode: area.mainCode,
    areaSubCode: area.subCode,
    areaMainName: area.mainName,
    areaSubName: area.subName
  }
}

/**
 * (mainCode, subCode) 쌍이 지역 마스터에 실재하는지 검사한다.
 * 저장 직전 최후 방어선 — 어떤 경로로 채워진 값이든 여기서 걸러진다.
 * @param {string|number} mainCode
 * @param {string|number} subCode
 * @param {Array<object>} areaList - `getArea()` 응답
 * @returns {boolean}
 */
export function isValidArea(mainCode, subCode, areaList) {
  if (mainCode === '' || mainCode == null) return false
  if (subCode === '' || subCode == null) return false
  if (!Array.isArray(areaList)) return false

  return areaList.some((area) =>
    String(area.mainCode) === String(mainCode) && String(area.subCode) === String(subCode)
  )
}

/**
 * 다음(카카오) 우편번호 API 의 `sigunguCode` 로 지역을 해석한다.
 *
 * 마스터의 `sub_code` 가 곧 5자리 시군구 코드라 코드 직접 대조가 가능하다.
 * 마스터에 없는 코드(신설 행정구역 등)면 빈 값을 반환한다 — 추정해서 채우지 않는다.
 *
 * @param {string} sigunguCode - daum `data.sigunguCode` (예: '11560')
 * @param {Array<object>} areaList - `getArea()` 응답
 * @returns {{areaMainCode:string, areaSubCode:string, areaMainName:string, areaSubName:string}}
 */
export function resolveAreaBySigunguCode(sigunguCode, areaList) {
  const code = normalizeSpace(sigunguCode)
  if (!code || !Array.isArray(areaList)) return EMPTY_AREA

  const hit = areaList.find((area) => String(area.subCode) === code)
  return hit ? toResult(hit) : EMPTY_AREA
}

/**
 * 주소 문자열로 지역을 해석한다. 카카오를 거치지 않은 기존 고객 주소용.
 *
 * 정규식으로 행정구역을 "파싱"하지 않고, **마스터 항목이 주소에 실제로 등장하는지 대조**한다.
 * 그래서 결과는 항상 마스터 실재 값이며, 실패는 빈 값으로 드러난다.
 *   1) 주소 첫 토큰을 시/도 동치 그룹으로 바꿔 마스터 후보를 좁힌다
 *   2) 남은 주소가 어떤 `subName` 으로 시작하는지 보고, 가장 긴 것을 고른다
 *      ('성남시 분당구' 가 '성남시' 보다 우선 — 2단계 시군구를 잃지 않는다)
 *   3) 매칭이 하나도 없으면 단층 광역시 폴백을 본다 (아래 주석)
 *
 * @param {string} address - 도로명/지번 주소 (예: '서울 영등포구 여의서로 43')
 * @param {Array<object>} areaList - `getArea()` 응답
 * @returns {{areaMainCode:string, areaSubCode:string, areaMainName:string, areaSubName:string}}
 */
export function resolveAreaByAddress(address, areaList) {
  const addr = normalizeSpace(address)
  if (!addr || !Array.isArray(areaList) || areaList.length === 0) return EMPTY_AREA

  const head = addr.split(' ')[0]
  const rest = normalizeSpace(addr.slice(head.length))

  let best = null
  let bestLength = 0
  // 단층 광역시(세종특별자치시) — 시/군/구 레벨이 없어 마스터가 subName 에 시/도명을 그대로 넣고,
  // 주소에도 시/군/구가 등장하지 않는다. 일반 매칭이 하나도 없을 때만 쓰는 폴백이다.
  let singleTier = null

  areaList.forEach((area) => {
    if (!sidoMatches(area.mainName, head)) return

    const sub = normalizeSpace(area.subName)
    if (!sub) return
    if (sub === normalizeSpace(area.mainName)) {
      singleTier = area
      return
    }
    if (rest !== sub && !rest.startsWith(sub + ' ')) return

    if (sub.length > bestLength) {
      best = area
      bestLength = sub.length
    }
  })

  if (best) return toResult(best)
  return singleTier ? toResult(singleTier) : EMPTY_AREA
}

/**
 * 마스터에 아직 없는 신규 행정구역을 다음(카카오) 응답만으로 구성한다.
 *
 * 이 모듈의 다른 함수와 달리 **코드가 마스터 밖에서 온다.** 그래서 쓰는 곳을 카카오 경로로
 * 한정한다 — `sigunguCode` 는 주소 API 가 구조화해 준 필드이지 주소 문자열을 파싱한 결과가
 * 아니다. 2026-09-15 장애의 오염원(`'여의서로43'`)은 문자열 추정 경로에서 나왔고 그쪽은
 * 여전히 마스터 화이트리스트로 막혀 있다.
 *
 * 관문은 BE `AreaResolver` 와 같게 맞춘다. BE 가 거부할 값을 굳이 보내지 않기 위해서다:
 *   - `subCode` 5자리 숫자, `mainCode` 는 그 앞 2자리 (시군구 코드는 시도 코드로 시작)
 *   - `subName` 은 마스터에 기록될 값이라 형식·길이를 제한 (마스터 `sub_name` 20자)
 *   - **시/도가 마스터에 있어야 한다** — 시도명을 채울 신뢰할 수 있는 출처가 기존 행뿐이고,
 *     시도 신설은 수기 DDL 대상이다. 세종처럼 `sigungu` 가 빈 값이면 쓸 이름이 없어 실패한다.
 *
 * @param {string} sigunguCode - daum `data.sigunguCode`
 * @param {string} sigungu - daum `data.sigungu` (마스터에 등록될 시군구명)
 * @param {Array<object>} areaList - `getArea()` 응답
 * @returns {{areaMainCode:*, areaSubCode:string, areaMainName:*, areaSubName:string}} 구성 불가면 EMPTY_AREA
 */
export function resolveNewAreaFromPostcode(sigunguCode, sigungu, areaList) {
  const subCode = normalizeSpace(sigunguCode)
  const subName = normalizeSpace(sigungu)
  if (!SUB_CODE_FORMAT.test(subCode) || !SUB_NAME_FORMAT.test(subName)) return EMPTY_AREA
  if (!Array.isArray(areaList)) return EMPTY_AREA

  // 시도명은 같은 시도 코드를 쓰는 기존 행에서 가져온다 (daum 의 시도 표기는 약칭·정식명이 섞여 있다)
  const sameSido = areaList.find((area) => String(area.mainCode) === subCode.slice(0, 2))
  if (!sameSido) return EMPTY_AREA

  return {
    areaMainCode: sameSido.mainCode,
    areaSubCode: subCode,
    areaMainName: sameSido.mainName,
    areaSubName: subName
  }
}
